import { getApiClient } from "./apiClient";

// ─────────────────────────────────────────────────────────────────────────
// Document upload & management (#1013).
//
// Backend endpoints (Django/DRF, same auth/session as every other service):
//   POST   /documents/                  multipart upload
//   GET    /documents/                  paginated org library list
//   GET    /documents/{id}/download/    streamed file, Content-Disposition
//   GET    /documents/{id}/usages/      link usages for a document
//   PATCH  /documents/{id}/             edit title/description/doc_kind
//   DELETE /documents/{id}/             org-admin only, hard delete
//   POST   /document-links/             attach an existing doc to a target
//   DELETE /document-links/{id}/        detach a link
// ─────────────────────────────────────────────────────────────────────────

export type DocKind = "reference" | "candidate_evidence";

export type ExtractionStatus =
  | "pending"
  | "done"
  | "low_quality"
  | "unsupported_scanned"
  | "failed"
  | string;

export type LinkTargetType = "project" | "track_entity" | "kg_entity";

export interface DocumentLink {
  id: string;
  document: string;
  project: string | null;
  track_entity: string | null;
  kg_entity_id: string | null;
  kg_namespace: string | null;
  /** Raw `project:<uuid>` form; prefer target_label for display. */
  target: string;
  created_by: string;
  created_at: string;
  // Present on GET /documents/{id}/usages/ — enough to name the target and
  // deep-link to it. Optional because create/list responses omit them.
  target_type?: LinkTargetType;
  target_label?: string;
  project_id?: string | null;
  project_name?: string | null;
  track_id?: string | null;
  track_name?: string | null;
  entity_id?: string | null;
  entity_type?: string | null;
  /** Current decision state of a candidate — picks the stage to land on. */
  entity_state?: string | null;
}

export interface Document {
  id: string;
  organization: string;
  uploaded_by: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  content_hash: string;
  title: string;
  description: string;
  doc_kind: DocKind;
  extraction_status: ExtractionStatus;
  metadata: Record<string, any>;
  links: DocumentLink[];
  created_at: string;
  updated_at: string;
}

export interface DocumentListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Document[];
}

export interface UsagesResponse {
  count: number;
  links: DocumentLink[];
}

export interface UploadDocumentParams {
  file: File;
  title?: string;
  description?: string;
  docKind?: DocKind;
  projectId?: string;
  trackEntityId?: string;
  onUploadProgress?: (percent: number) => void;
}

export interface UploadDocumentResult {
  document: Document;
  // true on 201 (newly stored bytes), false on 200 (identical content
  // already existed in the org library — dedupe reused it & linked it here).
  created: boolean;
}

export interface UpdateDocumentPatch {
  title?: string;
  description?: string;
  doc_kind?: DocKind;
}

export type CreateLinkRequest =
  | { document: string; project: string }
  | { document: string; track_entity: string }
  | { document: string; kg_entity_id: string; kg_namespace: string };

export const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024; // 50MB, matches backend default

// Accepted file extensions for the upload <input accept=""> — pdf plus
// common reference/evidence document types.
// PDF only: it is the sole format with a text-extraction path today, and an
// unextractable upload would never reach candidate evaluation. Office formats
// follow once extraction supports them.
export const ACCEPTED_DOCUMENT_EXTENSIONS = ".pdf";

class DocumentsApiService {
  async uploadDocument(
    params: UploadDocumentParams,
  ): Promise<UploadDocumentResult> {
    const {
      file,
      title,
      description,
      docKind,
      projectId,
      trackEntityId,
      onUploadProgress,
    } = params;

    if (projectId && trackEntityId) {
      throw new Error(
        "uploadDocument: specify only one of projectId or trackEntityId",
      );
    }

    const formData = new FormData();
    formData.append("file", file);
    if (title) formData.append("title", title);
    if (description) formData.append("description", description);
    if (docKind) formData.append("doc_kind", docKind);
    if (projectId) formData.append("project_id", projectId);
    if (trackEntityId) formData.append("track_entity_id", trackEntityId);

    const client = await getApiClient();
    const response = await client.post("/documents/", formData, {
      // The shared client defaults to Content-Type: application/json, which
      // makes axios v1 serialize FormData bodies to JSON instead of sending
      // multipart. Overriding here lets axios set the multipart boundary.
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onUploadProgress
        ? (evt) => {
            const percent = evt.total
              ? Math.round((evt.loaded * 100) / evt.total)
              : 0;
            onUploadProgress(percent);
          }
        : undefined,
    });

    return { document: response.data, created: response.status === 201 };
  }

  async listDocuments(params?: {
    docKind?: DocKind;
    project?: string;
    trackEntity?: string;
    // Docs linked to this graph entity anywhere (global entity links plus
    // per-candidate links in any project).
    kgEntity?: string;
    page?: number;
  }): Promise<DocumentListResponse> {
    const client = await getApiClient();
    const query: Record<string, string | number> = {};
    if (params?.docKind) query.doc_kind = params.docKind;
    if (params?.project) query.project = params.project;
    if (params?.trackEntity) query.track_entity = params.trackEntity;
    if (params?.kgEntity) query.kg_entity = params.kgEntity;
    if (params?.page) query.page = params.page;

    const response = await client.get("/documents/", { params: query });
    return response.data;
  }

  // Triggers a browser download of the streamed file, using the filename
  // from Content-Disposition when present (falls back to the doc's known
  // filename / a generic name otherwise).
  async downloadDocument(id: string, fallbackFilename?: string): Promise<void> {
    const client = await getApiClient();
    const response = await client.get(`/documents/${id}/download/`, {
      responseType: "blob",
    });

    let filename = fallbackFilename || `document-${id}`;
    const disposition = response.headers?.["content-disposition"] as
      | string
      | undefined;
    if (disposition) {
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
      const plainMatch = disposition.match(/filename="?([^";\n]+)"?/i);
      const raw = utf8Match?.[1] || plainMatch?.[1];
      if (raw) {
        try {
          filename = decodeURIComponent(raw);
        } catch {
          filename = raw;
        }
      }
    }

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async getUsages(id: string): Promise<UsagesResponse> {
    const client = await getApiClient();
    const response = await client.get(`/documents/${id}/usages/`);
    return response.data;
  }

  async updateDocument(
    id: string,
    patch: UpdateDocumentPatch,
  ): Promise<Document> {
    const client = await getApiClient();
    const response = await client.patch(`/documents/${id}/`, patch);
    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/documents/${id}/`);
  }

  async createLink(data: CreateLinkRequest): Promise<DocumentLink> {
    const client = await getApiClient();
    const response = await client.post("/document-links/", data);
    return response.data;
  }

  async deleteLink(id: string): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/document-links/${id}/`);
  }
}

export const documentsApi = new DocumentsApiService();

// ── Small shared display helpers ────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

export const DOC_KIND_LABELS: Record<DocKind, string> = {
  reference: "Reference",
  candidate_evidence: "Candidate Evidence",
};

export const EXTRACTION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  done: "Ready",
  low_quality: "Ready (low quality)",
  unsupported_scanned: "Needs OCR (not yet supported)",
  failed: "Extraction failed",
};
